import { useDebounceFn } from 'ahooks';
import { Input, InputProps } from 'antd';
import { trim } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { QUANTITY_DEBOUNCE_MILLISECOND } from 'utils/constants';
import { isFloat } from 'utils/misc';

type ValueType = InputProps['value'];

interface Props extends Omit<InputProps, 'onChange' | 'onBlur'> {
  isInteger?: boolean;
  min?: number;
  max?: number;
  onChange?: (val: number) => void;
  onBlur?: (val: number) => void;
  value?: number;
  onEmpty?: () => void;
  allowEmpty?: boolean;
  /* an empty field is a value of its own for this caller (no price for this quantity), so blur
     leaves it empty instead of reading it back as 0. The empty is reported through onChange/onEmpty,
     onBlur is for the numeric value and does not fire for it. */
  keepEmptyOnBlur?: boolean;
  precision?: number;
  /**
   * Decimals to *show* while the field is not being edited, without touching the value itself.
   *
   * Unlike `precision`, which rounds what is stored, this only shortens what is read. A size
   * entered in millimetres is kept as six decimals of inches - 888mm is 34.96063in - because that
   * is what makes it come back as 888 rather than 887.98, and a reader does not need to see the
   * tail. Focusing the field reveals the whole number, so what is edited is what is held.
   */
  displayPrecision?: number;
}

export const RevxInputNumber = ({
  isInteger,
  min,
  max,
  precision,
  displayPrecision,
  keepEmptyOnBlur,
  ...props
}: Props) => {
  const [realVal, setRealVal] = useState<ValueType>(props.value);
  const [displayVal, setDisplayVal] = useState<ValueType>(props.value);
  /**
   * Whether the field is being edited, so the effect below leaves it alone.
   *
   * Typing fires onChange, which updates the form, which comes back as a new `props.value` - and
   * the effect then shortened what had just been typed. Entering 8.1234 showed 8.123 while the
   * form held 8.1234, so the field disagreed with what it was about to save.
   */
  const editing = useRef(false);
  const valuePattern = isInteger ? /^-?\d+$/ : /^-?\d+\.?\d*$/;

  /** The value as it reads when nobody is editing it. Number() drops the trailing zeros. */
  const shortened = useCallback(
    (value: ValueType): ValueType => {
      if (displayPrecision === undefined || value === '' || value == null) {
        return value;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric)
        ? Number(numeric.toFixed(displayPrecision))
        : value;
    },
    [displayPrecision]
  );

  // const title = useMemo(() => {
  //   if (displayVal) {
  //     return formatNumber(displayVal + '');
  //   }
  // }, [displayVal])

  const { run: updateMarkup, cancel: cancelUpdateMarkup } = useDebounceFn(
    async (payload?: number) => {
      if (payload == null || isNaN(payload)) {
        setRealVal('');
        setDisplayVal('');
        return;
      }
      setRealVal(Number(payload)?.toFixed(precision || 2));
      setDisplayVal(Number(payload)?.toFixed(precision || 2));
    },
    { wait: QUANTITY_DEBOUNCE_MILLISECOND }
  );

  useEffect(() => {
    const val = props.value == null ? '' : props.value;
    setRealVal(val);
    // Never shorten under the cursor - see `editing`.
    setDisplayVal(editing.current ? val : shortened(val));
    if (props.suffix === '$' || props.prefix === '$') {
      cancelUpdateMarkup();
      updateMarkup(props.value);
    }
  }, [
    props.value,
    props.prefix,
    props.suffix,
    cancelUpdateMarkup,
    updateMarkup,
    shortened,
  ]);

  return (
    // <Tooltip
    //   trigger={['focus']}
    //   title={title}
    //   placement="topLeft"
    //   overlayClassName="numeric-input"
    // >
    <Input
      {...props}
      value={displayVal}
      onChange={(e) => {
        // A comma is how most of the world writes a decimal point, and this field is now shown
        // to people working in millimetres. It used to match neither the float check nor the
        // value pattern, so "3,5" fell through every branch and left the field empty with no
        // message - the value simply vanished as it was typed.
        const val = trim(e.target.value).replace(',', '.');
        if (val === '-') {
          if (!min || min < 0) {
            // allow negative number
            setDisplayVal(val);
            return;
          }
        }
        if (!isFloat(val, precision)) {
          return;
        }
        if (valuePattern.test(val)) {
          const numberVal =
            val.includes('.') &&
            ['.', '0'].includes(val.substr(val.length - 1, 1)) &&
            !isInteger
              ? val
              : +val;
          if (
            (min === undefined || numberVal >= min) &&
            (max === undefined || numberVal <= max)
          ) {
            setDisplayVal(numberVal);
            setRealVal(numberVal);
            if (props.onChange) {
              props.onChange(Number(numberVal));
            }
          }
        }

        if (!val) {
          // allow to input empty string so that the user can edit the number using a free style
          setDisplayVal(val);
          if (props.allowEmpty) {
            if (props.onChange) {
              props.onChange(null as any);
            }
            if (props.onEmpty) {
              props.onEmpty();
            }
          }
        } else {
          // do not allow input other invalid chars
        }
      }}
      onFocus={(e) => {
        // What is held, not what is read: the field is about to be edited, and editing a
        // shortened copy would write the shortened copy back.
        editing.current = true;
        setDisplayVal(realVal);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        editing.current = false;
        const isEmpty =
          displayVal === '' || displayVal === null || displayVal === undefined;
        if (isEmpty && keepEmptyOnBlur) {
          setRealVal('');
          return;
        }
        const value =
          precision && realVal
            ? Number(realVal || 0)?.toFixed(precision)
            : Number(realVal || 0);
        // Shortened for reading only. onBlur still reports the value that is held, so nothing
        // downstream ever receives the rounded copy.
        setDisplayVal(shortened(value));

        if (props.onBlur) {
          props.onBlur(Number(value || 0));
        }
      }}
    />
    // </Tooltip>
  );
};
